import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Paper,
  Alert,
  Checkbox,
  FormGroup
} from '@material-ui/core';
import { questionnaireApi } from '../api/questionnaireApi';

const QuestionnairePublic = () => {
  const { id, learnerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const tenant = queryParams.get('tenant');
        const sessionId = queryParams.get('session_id');
        const res = await questionnaireApi.fetchPublic(id, learnerId, tenant, sessionId);
        if (res.status === true) {
          setQuestionnaire(res.data);
          const initialAnswers = {};
          res.data.questions.forEach(q => {
            initialAnswers[q.id] = '';
          });
          setAnswers(initialAnswers);
        } else {
          setError(res.message || 'Questionnaire introuvable.');
        }
      } catch (err) {
        setError('Une erreur est survenue lors du chargement.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const tenant = queryParams.get('tenant');
      const contextSessionId = queryParams.get('session_id');
      const contextFormationId = queryParams.get('formation_id');

      const payload = {
        questionnaire_id: id,
        customer_id: learnerId,
        answers: answers,
        session_id: contextSessionId,
        formation_id: contextFormationId
      };
      const res = await questionnaireApi.submitQuestionnaire(payload, tenant);
      if (res.status) {
        setSubmitted(true);
      } else {
        setError(res.message || 'Erreur lors de l\'envoi.');
      }
    } catch (err) {
      setError('Impossible d\'envoyer vos réponses.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#F0EBF8">
        <CircularProgress style={{ color: '#673AB7' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="flex-start" minHeight="100vh" bgcolor="#F0EBF8" pt={8}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid #dadce0', borderTop: '8px solid #d93025' }}>
            <Typography variant="h4" gutterBottom sx={{ fontSize: '32px', color: '#202124', fontFamily: '"Google Sans", Roboto, Arial, sans-serif' }}>
              Attention
            </Typography>
            <Typography variant="body1" sx={{ color: '#202124', fontSize: '14px', mt: 2 }}>
              {error}
            </Typography>
            <Box mt={4}>
              <Button 
                variant="contained" 
                onClick={() => {
                  if (!questionnaire) {
                    window.location.reload();
                  } else {
                    setError(null);
                  }
                }}
                sx={{ 
                  bgcolor: '#673AB7', 
                  color: '#fff', 
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#5e35b1' } 
                }}
              >
                {questionnaire ? 'Fermer' : 'Réessayer'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box display="flex" justifyContent="center" alignItems="flex-start" minHeight="100vh" bgcolor="#F0EBF8" pt={8}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid #dadce0', borderTop: '8px solid #673AB7' }}>
            <Typography variant="h4" gutterBottom sx={{ fontSize: '32px', color: '#202124', fontFamily: '"Google Sans", Roboto, Arial, sans-serif' }}>
              {questionnaire.name}
            </Typography>
            <Typography variant="body1" sx={{ color: '#202124', fontSize: '14px', mt: 2 }}>
              Votre réponse a bien été enregistrée.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" sx={{ backgroundColor: '#F0EBF8', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', py: 4 }}>
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          
          {/* Header Card */}
          <Paper 
            elevation={0}
            sx={{ 
              mb: 2, 
              bgcolor: '#fff', 
              borderRadius: 2,
              border: '1px solid #dadce0',
              borderTop: '8px solid #673AB7',
              borderLeft: '6px solid transparent'
            }}
          >
            <Box p={3} pt={4}>
              <Typography variant="h3" sx={{ fontSize: '32px', color: '#202124', mb: 1, fontFamily: '"Google Sans", Roboto, Arial, sans-serif', lineHeight: '40px' }}>
                {questionnaire.name}
              </Typography>
              <Typography variant="body1" sx={{ color: '#202124', fontSize: '14px', mt: 2 }}>
                {questionnaire.formation_name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#d93025', display: 'block', mt: 2, fontSize: '13px' }}>
                * Indique une question obligatoire
              </Typography>
            </Box>
          </Paper>

          {/* Questions */}
          {questionnaire.questions.map((q, index) => {
            const options = typeof q.options === 'string' ? JSON.parse(q.options) : q.options;
            
            return (
              <Paper 
                key={q.id}
                elevation={0}
                sx={{ 
                  mb: 1.5, 
                  p: 3, 
                  bgcolor: '#fff', 
                  borderRadius: 2,
                  border: '1px solid #dadce0',
                  borderLeft: '6px solid transparent',
                  transition: 'box-shadow 0.2s',
                  '&:focus-within': {
                    boxShadow: '0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12)',
                    borderLeft: '6px solid #4285f4'
                  }
                }}
              >
                <Box mb={2}>
                  <Typography sx={{ fontSize: '16px', color: '#202124', fontFamily: 'Roboto, Arial, sans-serif' }}>
                    {q.text}
                    <Box component="span" sx={{ color: '#d93025', ml: 0.5 }}>*</Box>
                  </Typography>
                </Box>

                {q.type === 'qcm' || q.type === 'mcq' ? (
                  <FormGroup>
                    {options && options.map((opt, idx) => {
                      const optText = typeof opt === 'object' ? (opt.text || '') : opt;
                      const optValue = typeof opt === 'object' ? (opt.text || '') : opt;
                      const getCurrentAnswers = (ans) => {
                        if (!ans) return [];
                        try {
                          const parsed = JSON.parse(ans);
                          return Array.isArray(parsed) ? parsed : [ans];
                        } catch (e) {
                          return ans.split(',');
                        }
                      };
                      const currentAnswers = getCurrentAnswers(answers[q.id]);
                      const isChecked = currentAnswers.includes(optValue);
                      
                      return (
                        <FormControlLabel
                          key={idx}
                          control={
                            <Checkbox 
                              checked={isChecked}
                              onChange={(e) => {
                                let newAnswers;
                                if (e.target.checked) {
                                  newAnswers = [...currentAnswers, optValue];
                                } else {
                                  newAnswers = currentAnswers.filter(a => a !== optValue);
                                }
                                handleAnswerChange(q.id, JSON.stringify(newAnswers));
                              }}
                              color="primary" 
                              sx={{ 
                                color: '#5f6368', 
                                p: 1,
                                '&.Mui-checked': { color: '#673AB7' } 
                              }} 
                            />
                          }
                          label={<Typography sx={{ fontSize: '14px', color: '#202124' }}>{optText}</Typography>}
                          sx={{ ml: -1, mb: 0.5 }}
                        />
                      );
                    })}
                  </FormGroup>
                ) : (
                  <Box mt={1} width={{ xs: '100%', sm: '70%' }}>
                    <TextField
                      fullWidth
                      multiline={true}
                      placeholder="Votre réponse"
                      value={answers[q.id] || ''}
                      onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                      variant="standard"
                      InputProps={{
                        disableUnderline: false,
                        style: { fontSize: '14px', color: '#202124', paddingBottom: '4px' }
                      }}
                      sx={{
                        '& .MuiInput-underline:before': { borderBottom: '1px dotted #dadce0' },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: '1px solid #dadce0' },
                        '& .MuiInput-underline:after': { borderBottom: '2px solid #673AB7' }
                      }}
                    />
                  </Box>
                )}
              </Paper>
            );
          })}

          <Box mt={2} mb={4} display="flex" justifyContent="flex-start">
            <Button
              type="submit"
              disabled={isSubmitting}
              sx={{
                bgcolor: '#673AB7',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                px: 3,
                py: 1,
                borderRadius: '4px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#5e35b1',
                  boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)'
                },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Envoyer'}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
};

export default QuestionnairePublic;
